import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { sendMessage, subscribeMessages } from "../projects/api";

const fmtTime = (ts) => {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  return same
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const MessagesThread = ({ projectId, projectTitle, readOnly = false }) => {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    return subscribeMessages(projectId, setMessages);
  }, [projectId]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !user || sending) return;
    setSending(true);
    try {
      await sendMessage(projectId, {
        body,
        author: {
          uid: user.uid,
          name: profile?.displayName || user.displayName,
          email: user.email,
        },
      });
      setDraft("");
    } finally {
      setSending(false);
    }
  };

  if (!projectId) {
    return <div className="fw-empty">Select a conversation</div>;
  }

  return (
    <div className="fw-thread">
      {projectTitle && <div className="fw-thread__head">{projectTitle}</div>}
      <div className="fw-thread__scroll" ref={scrollerRef}>
        {messages.length === 0 ? (
          <div className="fw-thread__empty">No messages yet — say hello.</div>
        ) : (
          messages.map((m) => {
            const mine = m.authorUid === user?.uid;
            return (
              <div key={m.id} className={`fw-msg ${mine ? "fw-msg--mine" : ""}`}>
                <div className="fw-msg__meta">
                  <span className="fw-msg__author">{m.authorName || "User"}</span>
                  <span className="fw-msg__time">{fmtTime(m.createdAt)}</span>
                </div>
                <div className="fw-msg__body">{m.body}</div>
              </div>
            );
          })
        )}
      </div>
      {readOnly ? (
        <div className="fw-thread__composer" style={{ fontSize: 12, color: "var(--fw-muted)" }}>
          You have view-only access to this workspace.
        </div>
      ) : (
        <form className="fw-thread__composer" onSubmit={handleSend}>
          <textarea
            className="fw-textarea"
            placeholder="Write a message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(e);
            }}
            rows={2}
          />
          <button type="submit" className="fw-btn fw-btn--primary" disabled={sending || !draft.trim()}>
            {sending ? "Sending…" : "Send"}
          </button>
        </form>
      )}
    </div>
  );
};

export default MessagesThread;
